import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { 
  Download, FileText, FileSpreadsheet, File, 
  Loader2, CheckCircle2 
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToPDF, exportToExcel, exportToCSV } from '../services/exportService';

export const ExportButton = ({ 
  data = [], 
  columns = [], 
  title = 'Relatório',
  filename = 'relatorio',
  tenant = '',
  generatedBy = '',
  variant = 'default',
  size = 'default',
  className = ''
}) => {
  const [exporting, setExporting] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleExport = async (type) => {
    if (data.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }

    setExporting(type);
    setSuccess(null);

    try {
      const options = {
        title,
        filename,
        columns,
        tenant,
        generatedBy,
        subtitle: `Total de ${data.length} registros`
      };

      await new Promise(resolve => setTimeout(resolve, 500)); // UX delay

      switch (type) {
        case 'pdf':
          exportToPDF(data, options);
          toast.success('PDF exportado com sucesso!');
          break;
        case 'excel':
          exportToExcel(data, options);
          toast.success('Excel exportado com sucesso!');
          break;
        case 'csv':
          exportToCSV(data, options);
          toast.success('CSV exportado com sucesso!');
          break;
        default:
          break;
      }
      
      setSuccess(type);
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erro ao exportar. Tente novamente.');
    } finally {
      setExporting(null);
    }
  };

  const getIcon = (type) => {
    if (exporting === type) return <Loader2 className="w-4 h-4 animate-spin" />;
    if (success === type) return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    
    switch (type) {
      case 'pdf': return <FileText className="w-4 h-4 text-rose-500" />;
      case 'excel': return <FileSpreadsheet className="w-4 h-4 text-emerald-600" />;
      case 'csv': return <File className="w-4 h-4 text-blue-500" />;
      default: return <Download className="w-4 h-4" />;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size}
          className={`gap-2 ${className}`}
          data-testid="export-btn"
        >
          <Download className="w-4 h-4" />
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
          Escolha o formato de exportação
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={() => handleExport('pdf')}
          disabled={exporting !== null}
          className="cursor-pointer"
          data-testid="export-pdf-btn"
        >
          {getIcon('pdf')}
          <div className="ml-3 flex-1">
            <p className="font-medium">PDF</p>
            <p className="text-xs text-slate-500">Documento formatado</p>
          </div>
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleExport('excel')}
          disabled={exporting !== null}
          className="cursor-pointer"
          data-testid="export-excel-btn"
        >
          {getIcon('excel')}
          <div className="ml-3 flex-1">
            <p className="font-medium">Excel (.xlsx)</p>
            <p className="text-xs text-slate-500">Planilha editável</p>
          </div>
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleExport('csv')}
          disabled={exporting !== null}
          className="cursor-pointer"
          data-testid="export-csv-btn"
        >
          {getIcon('csv')}
          <div className="ml-3 flex-1">
            <p className="font-medium">CSV</p>
            <p className="text-xs text-slate-500">Dados separados por vírgula</p>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButton;
